/**
 * Solidified 120-instalment business logic & calculations (pure domain, zero framework imports).
 * Conforms to DEVELOPMENT_PLAN.md §6.3 and PRD §3.
 */
import type { InstallmentDto } from '../contracts/api.contracts';
import { InstallmentStatus } from '../enums/SystemEnums';
import {
  INSTALLMENT_STATUS_TABLE,
  getInstallmentStatusSpec,
  isInstallmentCleared,
  isInstallmentPayable,
  resolveEffectiveInstallmentStatus,
  type InstallmentDisplayStatus,
  type InstallmentStatusSpec,
} from '../installments/installmentStatusTable';

export {
  INSTALLMENT_STATUS_TABLE,
  getInstallmentStatusSpec,
  isInstallmentCleared,
  isInstallmentPayable,
  resolveEffectiveInstallmentStatus,
  type InstallmentDisplayStatus,
  type InstallmentStatusSpec,
};

/** Fixed tenure invariant for the entire schedule (DEVELOPMENT_PLAN.md §6.3). */
export const TENOR = 120;
export const TOTAL_MONTHS = TENOR;

/** Any (possibly absent) collection of installment rows — degrades, never throws. */
export type InstallmentRows = Array<InstallmentDto | null | undefined> | null | undefined;

/** Settled months: paid, or prepaid in advance. */
export function isPaidStatus(status: InstallmentStatus): boolean {
  return isInstallmentCleared(status);
}

/** Past-due months (inside the notice window or defaulted) — these are arrears. */
export function isArrearsStatus(status: InstallmentStatus): boolean {
  return status === InstallmentStatus.IN_NOTICE_PERIOD || status === InstallmentStatus.DEFAULTED;
}

/** Every month that still needs money: billed, past due, or not yet billed. */
export function isUnpaidStatus(status: InstallmentStatus): boolean {
  return !isPaidStatus(status);
}

/** Legacy row-level phase (Phase 2). */
export type InstallmentPhase = 'paid' | 'overdue' | 'pending' | 'unknown';

export function installmentPhase(row: InstallmentDto | undefined | null): InstallmentPhase {
  if (!row) return 'unknown';
  switch (row.status) {
    case InstallmentStatus.PAID:
    case InstallmentStatus.ADVANCED:
      return 'paid';
    case InstallmentStatus.IN_NOTICE_PERIOD:
    case InstallmentStatus.DEFAULTED:
      return 'overdue';
    case InstallmentStatus.UPCOMING:
    case InstallmentStatus.DUE:
      return 'pending';
    default:
      return 'unknown';
  }
}

export function countByPhase(rows: InstallmentRows): Record<InstallmentPhase, number> {
  const counts: Record<InstallmentPhase, number> = { paid: 0, overdue: 0, pending: 0, unknown: 0 };
  for (const row of rows ?? []) counts[installmentPhase(row)] += 1;
  return counts;
}


// --- Visual grid phases ------------------------------------------------------

export type GridPhase = 'cleared' | 'advance' | 'due' | 'delinquent' | 'upcoming' | 'unknown';

/**
 * Derive the rendered grid phase for an installment row.
 * Respects the interim safety net: UPCOMING && dueDate <= today (Asia/Dhaka) -> due.
 * The client never otherwise re-classifies from its clock.
 */
export function gridPhase(row: InstallmentDto | undefined | null, now: Date = new Date()): GridPhase {
  if (!row || typeof row !== 'object') return 'unknown';
  const effective = resolveEffectiveInstallmentStatus(row.status, row.dueDate, now);
  switch (effective) {
    case InstallmentStatus.PAID:
      return 'cleared';
    case InstallmentStatus.ADVANCED:
      return 'advance';
    case InstallmentStatus.DUE:
    case InstallmentStatus.IN_NOTICE_PERIOD:
      return 'due';
    case InstallmentStatus.DEFAULTED:
      return 'delinquent';
    case InstallmentStatus.UPCOMING:
      return 'upcoming';
    default:
      return 'unknown';
  }
}

export function countByGridPhase(
  rows: InstallmentRows,
  now: Date = new Date(),
): Record<GridPhase, number> {
  const counts: Record<GridPhase, number> = {
    cleared: 0, advance: 0, due: 0, delinquent: 0, upcoming: 0, unknown: 0,
  };
  for (const row of rows ?? []) counts[gridPhase(row, now)] += 1;
  return counts;
}

// --- In-range de-duplicated row filtering ------------------------------------

/**
 * Deduplicate rows with first-row-wins and filter to valid in-range (1..TENOR) installments.
 * Strictly mirrors the row set kept by buildGridSlots.
 */
export function getValidInstallmentRows(rows: InstallmentRows): InstallmentDto[] {
  const source = Array.isArray(rows) ? rows : [];
  const byNumber = new Map<number, InstallmentDto>();

  for (const raw of source) {
    if (!raw || typeof raw !== 'object') continue;
    const n = Number((raw as InstallmentDto).installmentNumber);
    if (!Number.isInteger(n) || n < 1 || n > TENOR) continue;
    if (byNumber.has(n)) continue;
    byNumber.set(n, raw as InstallmentDto);
  }

  return Array.from(byNumber.values()).sort(
    (a, b) => Number(a.installmentNumber) - Number(b.installmentNumber),
  );
}

// --- 120-slot grid model -----------------------------------------------------

export interface GridSlot {
  index: number;
  installmentNumber: number;
  row: InstallmentDto | null;
  phase: GridPhase;
  rowPhase: GridPhase;
  advanceCovered: boolean;
  duplicate: boolean;
  invalidAmount: boolean;
}

export interface GridOptions {
  now?: Date;
  advanceBalance?: number;
  monthlyAmount?: number;
}

/**
 * Builds exactly TENOR (120) slots:
 * - Denominator is always 120, never derived from API row count.
 * - Slots bound by installmentNumber (1..120), never by array index.
 * - Duplicates: first row wins, recorded in `duplicate: true`.
 * - Missing/gapped rows: row is null, phase is 'unknown' (neutral slot).
 * - Per §6.3 Q6/Q7: no simulated advance-preview coloring on unpaid rows;
 *   advanceCovered is true strictly for status === ADVANCED.
 */
export function buildGridSlots(
  rows: InstallmentRows,
  options: GridOptions = {},
): GridSlot[] {
  const now = options.now ?? new Date();
  const source = Array.isArray(rows) ? rows : [];
  const byNumber = new Map<number, InstallmentDto>();
  const duplicates = new Set<number>();

  for (const raw of source) {
    if (!raw || typeof raw !== 'object') continue;
    const n = Number((raw as InstallmentDto).installmentNumber);
    if (!Number.isInteger(n) || n < 1 || n > TENOR) continue;
    if (byNumber.has(n)) {
      duplicates.add(n);
      continue;
    }
    byNumber.set(n, raw);
  }

  const slots: GridSlot[] = [];
  for (let i = 0; i < TENOR; i++) {
    const num = i + 1;
    const row = byNumber.get(num) ?? null;
    const rowPhase: GridPhase = row ? gridPhase(row, now) : 'unknown';
    const isAdvanced = row?.status === InstallmentStatus.ADVANCED;

    slots.push({
      index: i,
      installmentNumber: num,
      row,
      phase: rowPhase,
      rowPhase,
      advanceCovered: isAdvanced,
      duplicate: duplicates.has(num),
      invalidAmount: row ? !Number.isFinite(row.amount) : false,
    });
  }
  return slots;
}

export function tallySlots(slots: GridSlot[]): Record<GridPhase, number> {
  const counts: Record<GridPhase, number> = {
    cleared: 0, advance: 0, due: 0, delinquent: 0, upcoming: 0, unknown: 0,
  };
  for (const slot of Array.isArray(slots) ? slots : []) counts[slot.phase] += 1;
  return counts;
}

// --- Solidified financial formulas (DEVELOPMENT_PLAN.md §6.3 Q5 & Q9) ----------

export interface AmountDueNowResult {
  overdueAmount: number;
  advanceCredit: number;
  amountDueNow: number;
  payableRows: InstallmentDto[];
}

export interface PrepaidView {
  /** Main figure: ADVANCED rows + leftover credit (falls back to `advanceBalance` when the API omits the field). */
  amount: number;
  /** Months prepaid (ADVANCED rows); 0 when the API omits the field. */
  months: number;
  /** Unallocated remainder (`advanceBalance`), shown as a small secondary line when > 0. */
  leftover: number;
}

/**
 * Member-facing advance figure (owner decision, supersedes §6.3 Q5/Q7 "remainder only"): the amount actually
 * prepaid, i.e. `prepaidAmount` (Σ ADVANCED rows + advanceBalance) from E07/E20. Tolerant of an older backend:
 * without the field it shows `advanceBalance` exactly as before. Never used for the BD-20 "Amount due now".
 */
export function resolvePrepaid(src: { prepaidAmount?: unknown; prepaidMonths?: unknown; advanceBalance?: unknown }): PrepaidView {
  const leftover = Math.max(0, Number(src.advanceBalance) || 0);
  const hasPrepaid = src.prepaidAmount !== undefined && src.prepaidAmount !== null && Number.isFinite(Number(src.prepaidAmount));
  const amount = hasPrepaid ? Math.max(0, Number(src.prepaidAmount)) : leftover;
  const months = hasPrepaid ? Math.max(0, Math.floor(Number(src.prepaidMonths) || 0)) : 0;
  return { amount, months, leftover };
}

/**
 * Solidified "Amount due now" = Σ overdue rows − advanceBalance (BD-20 formula, §6.3 Q5).
 * Overdue rows include DUE, IN_NOTICE_PERIOD, DEFAULTED, plus UPCOMING rows matching
 * the interim safety net (dueDate <= today Asia/Dhaka). Floored at 0.
 */
export function calculateAmountDueNow(
  rows: InstallmentRows,
  advanceBalance: unknown,
  refDate: Date = new Date(),
): AmountDueNowResult {
  const advance = Math.max(0, Number(advanceBalance) || 0);
  const validRows = getValidInstallmentRows(rows);
  const payableRows = validRows.filter((r) => isInstallmentPayable(r.status, r.dueDate, refDate));
  const overdueAmount = payableRows.reduce((sum, r) => {
    const amt = Number.isFinite(r.amount) ? r.amount : 0;
    return sum + amt;
  }, 0);
  const amountDueNow = Math.max(0, overdueAmount - advance);
  return {
    overdueAmount,
    advanceCredit: advance,
    amountDueNow,
    payableRows,
  };
}

/**
 * Total paid per §6.3 Q9:
 * Consumes backend `totalPaid` from E07 IF present in the API response;
 * Graceful fallback: client-side Σ of PAID + ADVANCED row amounts, booking excluded.
 * Consumes only in-range (1..TENOR) de-duplicated rows.
 */
export function calculateTotalPaid(
  subscription?: { totalPaid?: number } | null,
  rows?: InstallmentRows,
): number {
  if (subscription && typeof subscription.totalPaid === 'number' && Number.isFinite(subscription.totalPaid)) {
    return subscription.totalPaid;
  }
  const validRows = getValidInstallmentRows(rows);
  return validRows.reduce((sum, row) => {
    if (!isPaidStatus(row.status)) return sum;
    return sum + (Number.isFinite(row.amount) ? row.amount : 0);
  }, 0);
}
